import { QFDate, QFDropdown, QFPassword, QFText } from "IZOX/BLOCKS/Libs/QForm/_gears";
import { ColorX, IconX, LocaleX, ResponsiveX } from "IZOX/STATIC";
import { HStack, Spacer, VStack, XButton, XIconButton, XImage, XText } from "IZOX/_X";
import { SysConfig } from "__SYSDefault/Config";
import Styles from "__SYSDefault/Styles";
import _ from "lodash";

class QUICKUI {

  /**
   * @typedef {import("IZOX/__typedef/common").lang} lang
   * @typedef {import("IZOX/_X/VStack").vstackparams} vstackparams
   */

  /**
   * 
   * @param {lang} label 
   * @param {Number} fontSize 
   * @param {vstackparams} vstackparams 
   * @returns 
   */
  static NoDocs(label, fontSize = 12, vstackparams = {}){
    return (
      <VStack spacing={5} pV={30} {...vstackparams}>
        <XImage source={"/Images/home/noData.png"}/>
        <XText color={ColorX.CSS("Stone")} fontSize={fontSize}>
          {LocaleX.Parse(label)}
        </XText>
      </VStack>
    );
  }

  /**
   * @typedef {Object} popupparams
   * @property {lang} backCap
   * @property {lang} nextCap
   * @property {Function} validateBack
   * @property {Function} validateNext
   * 
   * @param {Function} backFunc 
   * @param {Function} nextFunc 
   * @param {popupparams} param2 
   * @returns 
   */
  static PopupFooterButton(backFunc, nextFunc, 
    {
      backCap = { EN: "Close", TC: "關閉" },
      nextCap = { EN: "Next", TC: "下一步"},
      validateBack = null,
      validateNext = null
    },
    disabled = false
  ){
    return (
      <HStack spacing={16} flexShrink={0}
        style={{
          borderTopWidth: 1, 
          borderTopStyle: "solid",
          borderTopColor: ColorX.CSS("greyOut", 0.3)
        }} 
        HAlign="]" p={16}>
        <XButton fitWidthToContent onPress={backFunc}
          disabled={disabled || (validateBack ? !validateBack() : false)}
          color={ColorX.CSS("Axit")}>
          <HStack>
            <XText bold>
              {LocaleX.Parse(backCap)}
            </XText>
          </HStack>
        </XButton>
        <XButton {...Styles.QFBtn} width={200} onPress={nextFunc}
          disabled={disabled || (validateNext ? !validateNext() : false)}
          padding={0} pV={8}>
          <HStack>
            <XText bold>
              {LocaleX.Parse(nextCap)}
            </XText>
          </HStack>
        </XButton>
      </HStack>
    );
  }

  /**
   * @typedef {Object} param
   * @property {Boolean} starred
   * @property {Boolean} readOnly  
   * @property {lang} errorMessage 
   * 
   * @param {String} name 
   * @param {lang} label 
   * @param {param & import("IZOX/BLOCKS/Libs/QForm/_gears/QFText").qftextparams} param2 
   * @returns 
   */
  static WebTextField(name, label, 
    {
      starred = true, 
      readOnly = false, 
      width = 320, 
      placeholder = undefined,
      errorMessage = undefined,
      ...others
    } = {}, 
    inject = null){
    let fontSize = ResponsiveX.Split({0: () => 14, [SysConfig.WebUIWidth]: () => 14});
    return (
      <VStack width={width}>
        <HStack HAlign="[" spacing={4}>
          <XText fontSize={12} color={ColorX.CSS("Stone")}>
            {LocaleX.Parse(label)}
          </XText>
          {starred &&
            <XText fontSize={12} color={ColorX.CSS("Blood")}>{"*"}</XText>}
          {inject && <Spacer/>}
          {inject}
        </HStack>
        <QFText name={name} placeholder={LocaleX.Parse(placeholder ?? label)} 
          readOnly={readOnly}
          p={12}
          fontSize={fontSize}
          focus_bColor={ColorX.CSS("Axit")}
          bColor={ColorX.CSS("Gray5")}
          isError={() => !_.isEmpty(errorMessage)}
          {...others}/>
        {!_.isEmpty(errorMessage) &&
          <HStack HAlign="[" spacing={4} pV={4}>
            <XText fontSize={12} color={ColorX.CSS("Error")}>
              {LocaleX.Parse(errorMessage)}
            </XText>
          </HStack>}
      </VStack>
    );
  }

  /**
   * @typedef {Object} param
   * @property {Boolean} starred
   * @property {Boolean} readOnly
   * @property {lang} errorMessage
   * 
   * @param {String} name 
   * @param {lang} label 
   * @param {param & import("IZOX/BLOCKS/Libs/QForm/_gears/QFPassword").qfpasswordparams} param2 
   * @returns 
   */
  static WebPasswordField(name, label, 
    {
      starred = true, 
      readOnly = false, 
      width = 320, 
      placeholder = undefined,
      errorMessage = undefined,
      ...others
    } = {}, 
    inject = null){
    return (
      <VStack width={width}>
        <HStack HAlign="[" spacing={4}>
          <XText fontSize={12} color={ColorX.CSS("Stone")}>
            {LocaleX.Parse(label)}
          </XText>
          {starred &&
            <XText fontSize={12} color={ColorX.CSS("Blood")}>{"*"}</XText>}
          {inject && <Spacer/>}
          {inject}
        </HStack>
        <QFPassword name={name} placeholder={LocaleX.Parse(placeholder ?? label)} 
          readOnly={readOnly}
          p={12}
          bW={1}
          focus_bColor={ColorX.CSS("Axit")}
          bColor={ColorX.CSS("Gray5")}
          isError={() => !_.isEmpty(errorMessage)}
          {...others}/>
        {!_.isEmpty(errorMessage) &&
          <HStack HAlign="[" spacing={4} pV={4}>
            <XText fontSize={12} color={ColorX.CSS("Error")}>
              {LocaleX.Parse(errorMessage)}
            </XText>
          </HStack>}
      </VStack>
    );
  }

  /**
   * @typedef {Object} webdropdownparam
   * @property {Boolean} starred
   * @property {Boolean} readOnly
   * @property {Boolean} allowSearch
   * @property {(option) => void} onSelectChange
   * @property {String} capAccessor
   * @property {String} valAccessor
   * @property {lang} placeholder
   * @property {String | Number} width
   * @property {Boolean} multiple
   * @property {String} disabled_bgColor
   * @property {lang} errorMessage
   * 
   * @param {String} name 
   * @param {lang}} label 
   * @param {*[]} options 
   * @param {webdropdownparam} param3 
   * @returns 
   */
  static WebDropdown(name, label, options, 
    {
      starred = true, 
      readOnly = false,
      allowSearch = true,
      onSelectChange = undefined,
      capAccessor = "cap",
      valAccessor = "val",
      placeholder = undefined,
      width = "100%",
      multiple = false,
      disabled_bgColor = undefined,
      errorMessage = undefined,
    } = {}){
    return (
      <VStack width={width}>
        <HStack HAlign="[" spacing={4}>
          <XText fontSize={12} color={ColorX.CSS("Stone")}>
            {LocaleX.Parse(label)}
          </XText>
          {starred && <XText fontSize={12} color={ColorX.CSS("Blood")}>{"*"}</XText>}
        </HStack>
        <QFDropdown name={name}
          placeholder={LocaleX.Parse(placeholder)}
          readOnly={readOnly}
          options={options}
          capAccessor={capAccessor}
          valAccessor={valAccessor}
          disabled_bgColor={disabled_bgColor}
          p={12}
          bW={1}
          height={44}
          multiple={multiple}
          allowSearch={allowSearch}
          focus_bColor={ColorX.CSS("Axit")}
          bColor={ColorX.CSS("Gray5")}
          onSelectChange={onSelectChange}
          isError={() => !_.isEmpty(errorMessage)}/>
        {!_.isEmpty(errorMessage) &&
          <HStack HAlign="[" spacing={4} pV={4}>
            <XText fontSize={12} color={ColorX.CSS("Error")}>
              {LocaleX.Parse(errorMessage)}
            </XText>
          </HStack>}
      </VStack>
    );
  }

  /**
   * @typedef {Object} param
   * @property {Boolean} starred
   * @property {Boolean} readOnly   
   * @property {lang} errorMessage 
   * 
   * @param {String} name 
   * @param {lang} label 
   * @param {param & import("IZOX/BLOCKS/Libs/QForm/_gears/QFText").qftextparams} param2 
   * @returns 
   */
  static WithZipCode(name, label, 
    {
      starred = true, 
      readOnly = false, 
      width = 320, 
      placeholder = undefined,
      errorMessage = undefined,
      ...others
    } = {}){
    return (
      <VStack width={width}>
        <HStack HAlign="[" spacing={5}>
          <XText fontSize={12}>
            {LocaleX.Parse(label)}
          </XText>
          {starred && <XText fontSize={12} color={ColorX.CSS("Blood")}>{"*"}</XText>}
        </HStack>
        <HStack fullWidth spacing={13}>
          <HStack bgColor={ColorX.CSS("Gray5")} 
            bColor={ColorX.CSS("Gray5")}
            bW={1}
            fitWidthToContent pH={20} pV={10}>
            <XText fontSize={12} color={ColorX.CSS("grey")}>
              {"+852"}
            </XText>
          </HStack>
          <QFText fontSize={14}
            {...Styles.QFText}
            readOnly={readOnly}
            bColor={ColorX.CSS("Gray5")}
            focus_bColor={ColorX.CSS("Axit")}
            name={name}
            width={104}
            placeholder={LocaleX.Parse(placeholder ?? label)}
            isError={() => !_.isEmpty(errorMessage)}
            fullWidth
            numberOnly
            {...others}
            />
        </HStack>
        {!_.isEmpty(errorMessage) &&
          <HStack HAlign="[" spacing={4} pV={4}>
            <XText fontSize={12} color={ColorX.CSS("Error")}>
              {LocaleX.Parse(errorMessage)}
            </XText>
          </HStack>}
      </VStack>
    );  
  }

  /**
   * 
   * @param {lang} caption 
   * @returns 
   */
  static AFSectionLine(caption){
    return (
      <VStack fullWidth pB={24}>
        <HStack bgColor={ColorX.CSS("Axit")} pH={ResponsiveX.RefMax(48, 1070)} pV={12} HAlign="[">
          <XText color={ColorX.CSS("pureWhite")} fontSize={16} bold>
            {LocaleX.Parse(caption)}
          </XText>
        </HStack>
      </VStack>
    );
  }

  /**
   * @typedef {Object} param
   * @property {Boolean} starred
   * @property {Boolean} readOnly
   * @property {lang} errorMessage
   * 
   * @param {String} name 
   * @param {lang} label 
   * @param {param & import("IZOX/BLOCKS/Libs/QForm/_gears/QFDate").qfdateparams} param2 
   * @returns 
   */
  static WebDateField(name, label, 
    {
      starred = true, 
      readOnly = false, 
      width = 320, 
      placeholder = undefined,
      errorMessage = undefined,
      ...others
    } = {}, 
    inject = null){
    return (
      <VStack width={width}>
        <HStack HAlign="[" spacing={4}>
          <XText fontSize={12} color={ColorX.CSS("Stone")}>
            {LocaleX.Parse(label)}
          </XText>
          {starred &&
            <XText fontSize={12} color={ColorX.CSS("Blood")}>{"*"}</XText>}
          {inject && <Spacer/>}
          {inject}
        </HStack>
        <QFDate name={name} placeholder={LocaleX.Parse(placeholder ?? label)} 
          readOnly={readOnly}
          p={12}
          bW={1}
          height={44}
          calendarSize={213/7}
          focus_bColor={ColorX.CSS("Axit")}
          bColor={ColorX.CSS("Gray5")}
          isError={() => !_.isEmpty(errorMessage)}
          {...others}/>
        {!_.isEmpty(errorMessage) &&
          <HStack HAlign="[" spacing={4} pV={4}>
            <XText fontSize={12} color={ColorX.CSS("Error")}>
              {LocaleX.Parse(errorMessage)}
            </XText>
          </HStack>}
      </VStack>
    );
  }

  /**
   * @typedef {import("IZOX/STATIC/ColorX").colorName} colorName
   * 
   * @param {lang} label 
   * @param {colorName} bgColorName 
   * @param {colorName} [textColorName="pureWhite"] 
   * @returns 
   */
  static BoxTitle(label, bgColorName, textColorName = "pureWhite"){
    let fontSize = ResponsiveX.Split({0: () => 14, [SysConfig.WebUIWidth]: () => 16});
    return (
      <VStack bW={2} bColor={ColorX.CSS(bgColorName)} fullWidth>
        <HStack bgColor={ColorX.CSS(bgColorName)} HAlign="[" pH={16} pV={8}>
          <XText color={ColorX.CSS(textColorName)} bold fontSize={fontSize}>
            {LocaleX.Parse(label)}
          </XText>  
        </HStack>
      </VStack>
    );
  }

  /**
   * 
   * @param {lang} label 
   * @param {*} value 
   * @param {String | Number} width
   * @param {import("IZOX/_X/XText").xtextparams} labelprops 
   * @param {import("IZOX/_X/XText").xtextparams} textprops 
   * @param {*} inject 
   */
  static TextPreview(label, value, width = "50%", labelprops = {}, textprops = {}, inject = null){
    let fontSize = ResponsiveX.Split({0: () => 12, [SysConfig.WebUIWidth]: () => 14});
    return (
      <VStack width={width}>
        <HStack HAlign="[" spacing={4}>
          <XText fontSize={fontSize} color={ColorX.CSS("Gray4")} {...labelprops} flexShrink={0}>
            {LocaleX.Parse(label)}
          </XText>
          {inject}
        </HStack>
        <HStack HAlign="[" spacing={4}>
          <XText fontSize={fontSize} color={ColorX.CSS("Stone")} {...textprops}>
            {LocaleX.Parse(value)}
          </XText>
        </HStack>
      </VStack>
    );
  }

  /**
   * 
   * @param {lang} label 
   * @param {Function} closeFunc 
   * @returns 
   */
  static PopupTitleBar(label, closeFunc){
    return (
      <HStack pH={16} shadow>
        <XText fontSize={16} bold>
          {LocaleX.Parse(label)}
        </XText>
        <Spacer/>
        <XIconButton onPress={closeFunc}>
          {IconX.Get("ant", "close", 20, ColorX.CSS("Stone"))}
        </XIconButton>
      </HStack>
    );
  }

}

export default QUICKUI;